import { useGetSeriesForTarget } from '@/generated/client/diagram/diagram';
import type { getSeriesForTarget } from '@/generated/client/diagram/diagram';

import { TIME_SERIES_QUERY_CACHE_TIME_MS } from '../../config/queryConfig';
import type { DiagramSeriesGroup } from '../../types/DiagramSeries.types';
import { DISABLED_TIME_WINDOW } from '../time-window/timeWindow';
import type { TimeWindowMicros } from '../time-window/timeWindow';
import { mapDiagramSeriesMetadata } from './diagramSeriesMappers';

type UseMeasureChannelsQueryParams = {
  targetId: number | undefined;
  timeWindow: TimeWindowMicros | undefined;
};

type MeasureChannelsResponse = Awaited<ReturnType<typeof getSeriesForTarget>>;

// A module-level select keeps its reference stable, so TanStack Query only re-maps on new data.
function selectDiagramSeriesGroups(response: MeasureChannelsResponse): DiagramSeriesGroup[] {
  return mapDiagramSeriesMetadata(response.data);
}

/** Which series exist for the target in the selected window (speed, measure channels, states). */
export function useMeasureChannelsQuery({ targetId, timeWindow }: UseMeasureChannelsQueryParams) {
  const isEnabled = targetId !== undefined && timeWindow !== undefined;

  return useGetSeriesForTarget(
    // Orval requires a number, but enabled prevents this sentinel from ever reaching the API.
    targetId ?? 0,
    timeWindow ?? DISABLED_TIME_WINDOW,
    {
      query: {
        enabled: isEnabled,
        retry: false,
        staleTime: TIME_SERIES_QUERY_CACHE_TIME_MS,
        gcTime: TIME_SERIES_QUERY_CACHE_TIME_MS,
        select: selectDiagramSeriesGroups,
      },
    }
  );
}
